import { useState, useEffect, type ReactNode, useRef, useMemo } from 'react';
import useSWR from 'swr';
import apiClient from '../services/api';
import { notifySessionExpired } from '../services/sessionExpiry';
import { useAuth } from './AuthContext';
import { GenieContext, type Message } from './GenieContext';

export function GenieProvider({ children }: { children: ReactNode }) {
    const [messages, setMessages] = useState<Message[]>([]);
    const [isThinking, setIsThinking] = useState(false);
    const [thinkingStage, setThinkingStage] = useState<string | null>(null);
    const { isLoggedIn } = useAuth();

    const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

    // Use SWR for initial conversation setup
    const { data: conversationData, mutate } = useSWR(
        isLoggedIn ? '/genie/start_conversation' : null,
        (url) => apiClient.post(url).then(res => res.data),
        {
            revalidateOnFocus: false,
            shouldRetryOnError: false,
            onSuccess: (data) => {
                if (data.conversation_id) {
                    // Reset if conversation ID changes (session start)
                    setMessages([]);
                    setActiveConversationId(data.conversation_id);
                }
            }
        }
    );

    const conversationId = activeConversationId || conversationData?.conversation_id || null;

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const sendMessage = async (text: string) => {
        if (!text.trim()) return;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        const timestamp = new Date().toISOString();
        const userMsg: Message = {
            type: 'user',
            content: text,
            timestamp: formatTime(new Date()),
            messageId: `user-${timestamp}-${Math.random().toString(36).substr(2, 9)}`
        };

        setMessages(prev => [...prev, userMsg]);
        setIsThinking(true);
        setThinkingStage("Connecting to Genie");

        try {
            const extraHeaders: Record<string, string> = {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream'
            };
            if (conversationId) {
                extraHeaders['X-Conversation-Id'] = conversationId;
            }

            const response = await fetch('/api/genie/messages/stream', {
                method: 'POST',
                headers: extraHeaders,
                body: JSON.stringify({ message: text }),
                signal: abortControllerRef.current.signal
            });

            // 🌟 1. If HTTP status fails early, throw the entire response object instead of a generic string
            if (response.status === 401) {
                // Session or Entra token expired and cannot be refreshed silently
                const body = await response.json().catch(() => null);
                notifySessionExpired(body?.login_url);
                setIsThinking(false);
                setThinkingStage(null);
                return;
            }
            if (!response.ok) {
                throw response;
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            if (reader) {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const data = JSON.parse(line.slice(6));
                            if (data.type === 'info' && data.conversation_id) {
                                setActiveConversationId(data.conversation_id);
                            } else if (data.type === 'status') {
                                setThinkingStage(data.status);
                            } else if (data.type === 'result') {
                                const result = data.data;
                                const genieMsg: Message = {
                                    type: 'genie',
                                    content: result.reply,
                                    timestamp: formatTime(new Date()),
                                    suggestedQuestions: result.suggested_questions || [],
                                    messageId: result.message_id || `genie-${Date.now()}`,
                                    attachmentId: result.attachment_id,
                                    chartRecommendation: result.chart_recommendation || undefined,
                                };
                                setMessages(prev => [...prev, genieMsg]);
                                setIsThinking(false);
                                setThinkingStage(null);
                            } else if (data.type === 'error') {
                                // 🌟 2. For errors emitted mid-stream by the engine
                                throw new Error(data.message || "An error occurred mid-stream.");
                            }
                        }
                    }
                }
            }
        } 
        catch (error: any) {
            // 1. Immediately exit on manual user cancellations
            if (error.name === 'AbortError' || error.message === 'canceled') {
                console.log("Genie request canceled.");
                return;
            }

            console.error("Genie API error encountered:", error);

            // Create an async runtime task inside the catch block to unpack async streams
            const parseAndDisplayError = async () => {
                let displayMessage = "Error: Unable to process your request. Please try again.";

                // 2. Extract specific payload if the error is a thrown Fetch Response object
                if (error instanceof Response) {
                    try {
                        const responseText = await error.text();
                        console.error("Genie API Error Response Body:", responseText);

                        try {
                            const errorData = JSON.parse(responseText);
                            displayMessage = errorData.error ?? errorData.message ?? errorData.detail ?? responseText;
                        } catch {
                            // Text payload fallback
                            displayMessage = responseText || `Request failed with status ${error.status}`;
                        }
                    } catch {
                        displayMessage = `HTTP Error ${error.status}: ${error.statusText || 'Unknown Connection Failure'}`;
                    }
                } 
                // 3. Extract the message if it's a mid-stream thrown Error object or network crash
                else if (error instanceof Error) {
                    displayMessage = error.message;
                }

                // 4. Dispatch the rich descriptive message payload straight to user view
                const errorMsg: Message = {
                    type: 'genie',
                    content: displayMessage,
                    timestamp: formatTime(new Date()),
                    messageId: `error-${Date.now()}`
                };

                setMessages(prev => [...prev, errorMsg]);
                setIsThinking(false);
                setThinkingStage(null);
            };

            // Fire off the asynchronous resolution
            parseAndDisplayError();
        }
 finally {
            abortControllerRef.current = null;
        }
    };

    const restartConversation = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        setIsThinking(false);
        setThinkingStage(null);
        setMessages([]);
        setActiveConversationId(null);

        try {
            await apiClient.post('/genie/restart_conversation');
            mutate(); // Refresh conversation ID via SWR
        } catch (err) {
            console.error("Failed to restart conversation", err);
        }
    };

    const submitFeedback = async (messageId: string, isUseful: boolean) => {
        try {
            const config = conversationId ? { headers: { 'X-Conversation-Id': conversationId } } : {};
            await apiClient.post(`/genie/messages/${messageId}/feedback`, { is_useful: isUseful }, config);
        } catch (err) {
            console.error("Failed to submit feedback", err);
        }
    };

    const getDownloadUrl = async (messageId: string, attachmentId: string): Promise<boolean> => {
        try {
            const config = {
                responseType: 'blob' as const,
                ...(conversationId ? { headers: { 'X-Conversation-Id': conversationId } } : {})
            };
            const response = await apiClient.get(`/genie/messages/${messageId}/attachments/${attachmentId}/download`, config);

            const file = new Blob([response.data as BlobPart], { type: 'text/csv' });
            const fileURL = URL.createObjectURL(file);
            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', 'genie_result.csv');
            document.body.appendChild(fileLink);
            fileLink.click();
            document.body.removeChild(fileLink);
            URL.revokeObjectURL(fileURL);

            return true;
        } catch (err) {
            console.error("Failed to download file", err);
            return false;
        }
    };

    const value = useMemo(() => ({
        messages,
        isThinking,
        thinkingStage,
        conversationId,
        sendMessage,
        submitFeedback,
        getDownloadUrl,
        restartConversation
    }), [messages, isThinking, thinkingStage, conversationId]);

    return (
        <GenieContext.Provider value={value}>
            {children}
        </GenieContext.Provider>
    );
}
